import React, { useMemo } from 'react';
import {
  getLocalStorage,
  getSessionStorage,
  setLocalStorage,
  setSessionStorage,
} from 'src/functions/localStorageHandler';
import { StatsigClient } from '@statsig/js-client';
import { StatsigProvider } from '@statsig/react-bindings';
import { v4 } from 'uuid';
import { setStatsigClient } from './statsigClient';

const FIRST_TOUCH_TEXT = 'first time visitor';

declare global {
  interface Window {
    dataLayer?: Record<string, any>[];
  }
}

const setGA4UserId = (userId: string): void => {
  if (typeof window === 'undefined' || !userId) return;

  if (window.dataLayer) {
    window.dataLayer.push({ user_id: userId });
  }
};

interface VisitedPath {
  propertyName: string;
  propertyValue: string;
}

const VISITED_PATHS_TO_TRACK: Record<string, VisitedPath> = {
  '/reviews': {
    propertyName: 'visitedReviews',
    propertyValue: 'viewed Reviews page',
  },
  '/pro': {
    propertyName: 'visitedPro',
    propertyValue: 'viewed Pro page',
  },
  '/request-a-call-form': {
    propertyName: 'visitedRequestACallForm',
    propertyValue: 'viewed Request a Call Form page',
  },
};

const StatsigProviderMask: React.FC<any> = ({ children }) => {
  const getCurrentPathTrackCustomProperty = (path: string) => {
    if (!path) return {};

    if (VISITED_PATHS_TO_TRACK[path]) {
      const { propertyName, propertyValue } = VISITED_PATHS_TO_TRACK[path];

      const visitedPath = {
        [propertyName]: propertyValue,
      };

      setLocalStorage(`visitedPath:${propertyName}`, JSON.stringify(visitedPath));

      return visitedPath;
    }

    return {};
  };

  const getPersistedPathTrackCustomProperty = () => {
    const visitedPaths: Record<string, string> = {};

    Object.values(VISITED_PATHS_TO_TRACK).forEach(({ propertyName }) => {
      const visitedPathFromStorage = getLocalStorage(`visitedPath:${propertyName}`);

      if (!visitedPathFromStorage) return;

      const { [propertyName]: propertyValue } = JSON.parse(visitedPathFromStorage);

      visitedPaths[propertyName] = propertyValue;
    });

    return visitedPaths;
  };

  const client = useMemo(() => {
    if (typeof window === 'undefined') return null;

    const statsigUserId = getLocalStorage('statsigUserId');
    let userId = statsigUserId;
    let customProperties = {};

    const visitedPathsCustomProperty = {
      ...getPersistedPathTrackCustomProperty(),
      ...getCurrentPathTrackCustomProperty(window.location.pathname),
    };

    if (!statsigUserId) {
      userId = (window as any).v4 ? (window as any).v4() : v4();
      setLocalStorage('statsigUserId', userId);
      setSessionStorage('@statsig:firstTouch', 'true');
    }

    if (getSessionStorage('@statsig:firstTouch') === 'true') {
      customProperties = {
        firstTouch: FIRST_TOUCH_TEXT,
      };
    }

    customProperties = {
      ...customProperties,
      ...visitedPathsCustomProperty,
    };

    // Create client with user configuration
    const newClient = new StatsigClient(
      process.env.GATSBY_STATSIG_SDK_KEY || 'client-yMNyryucOI2ddIofgqohKtiJTW1PMsiovM05eN1gKDi',
      { userID: userId, custom: customProperties }
    );

    // Store client for global access BEFORE initialization
    setStatsigClient(newClient);

    // Send statsigUserId to GA4 for cross-platform user identification
    setGA4UserId(userId);

    return newClient;
  }, []);

  if (!client) return <>{children}</>;

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
};

export default StatsigProviderMask;
